/**
 * GPT-4 Vision OCR Helper
 * Uses OpenAI's GPT-4 Vision API to extract box score data from screenshots
 */

import { splitPlayerName, splitDbName, isNearLastName, isTotalsRow } from './nameNormalize';

// Use relative URL to work in both development and production
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Convert image file to base64, resizing to max 1280px to reduce upload size.
 * @param {File} imageFile - The image file
 * @returns {Promise<string>} - Base64 data URL
 */
const imageToBase64 = (imageFile) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(imageFile);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const MAX_WIDTH = 1920;
      let { width, height } = img;
      if (width > MAX_WIDTH) {
        height = Math.round((height * MAX_WIDTH) / width);
        width = MAX_WIDTH;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(imageFile);
    };
    img.src = objectUrl;
  });
};

/**
 * Check if GPT-4 Vision is available/configured
 * @returns {Promise<boolean>}
 */
export const isGPT4VisionAvailable = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/ocr/vision/status`);
    const data = await response.json();
    return data.available && data.configured;
  } catch (error) {
    console.error('❌ Failed to check GPT-4 Vision status:', error);
    return false;
  }
};

/**
 * Extract box score data using GPT-4 Vision
 * @param {File} imageFile - The screenshot image
 * @param {Array} players - List of players from database for matching
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Object>} - Extracted box score data
 */
export const extractWithGPT4Vision = async (imageFile, players = [], onProgress = null) => {
  try {
    if (onProgress) onProgress(0.1);

    // Convert image to base64
    const base64Image = await imageToBase64(imageFile);
    
    if (onProgress) onProgress(0.3);

    // Send to server for processing
    const response = await fetch(`${API_BASE_URL}/ocr/vision`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        image: base64Image
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'GPT-4 Vision request failed');
    }

    const result = await response.json();
    
    if (onProgress) onProgress(0.9);


    // Match players from database
    const matchedPlayers = result.data.players
      .filter(player => !isTotalsRow(player.name))
      .map(player => {
      const matched = matchPlayerByName(player.name, players);
      return {
        originalName: player.name,
        playerName: matched ? `${matched.firstName} ${matched.lastName}` : player.name,
        playerId: matched?.id || null,
        matchConfidence: matched?.confidence || 'low',
        stats: {
          minutes: player.minutes,
          points: player.points,
          rebounds: player.rebounds,
          assists: player.assists,
          steals: player.steals,
          blocks: player.blocks,
          turnovers: player.turnovers,
          fieldGoalsMade: player.fieldGoalsMade,
          fieldGoalsAttempted: player.fieldGoalsAttempted,
          threePointersMade: player.threePointersMade,
          threePointersAttempted: player.threePointersAttempted,
          freeThrowsMade: player.freeThrowsMade,
          freeThrowsAttempted: player.freeThrowsAttempted,
          offensiveRebounds: player.offensiveRebounds,
          fouls: player.fouls
        }
      };
    });

    if (onProgress) onProgress(1.0);

    return {
      teamName: result.data.teamName,
      players: matchedPlayers,
      errors: [],
      metadata: {
        tokensUsed: result.tokensUsed,
        estimatedCost: result.estimatedCost
      }
    };

  } catch (error) {
    console.error('GPT-4 Vision OCR Error:', error);
    throw new Error(`GPT-4 Vision failed: ${error.message}`);
  }
};

/**
 * Match OCR player name to database player.
 * Gleiche Zerlegung wie im Claude-Pfad: Vorname, Nachname, Zusatz getrennt,
 * damit "B. Portis Jr." nicht an "jr" scheitert.
 */
const matchPlayerByName = (ocrName, players) => {
  if (!players || players.length === 0) return null;

  const ocr = splitPlayerName(ocrName);
  if (!ocr.last) return null;

  const entries = players.map(player => ({ player, parts: splitDbName(player) }));

  const narrowBySuffix = (candidates) => {
    if (!ocr.suffix || candidates.length < 2) return candidates;
    const exact = candidates.filter(c => c.parts.suffix === ocr.suffix);
    return exact.length > 0 ? exact : candidates;
  };

  // Ausgeschriebener Vorname + Nachname
  if (ocr.first.length > 1) {
    const full = narrowBySuffix(entries.filter(e =>
      e.parts.first === ocr.first && e.parts.last === ocr.last
    ));
    if (full.length > 0) {
      return { ...full[0].player, confidence: 'high' };
    }
  }

  // Initial + Nachname
  if (ocr.first) {
    const byInitial = narrowBySuffix(entries.filter(e =>
      e.parts.first.charAt(0) === ocr.first.charAt(0) && e.parts.last === ocr.last
    ));
    if (byInitial.length === 1) {
      return { ...byInitial[0].player, confidence: 'high' };
    } else if (byInitial.length > 1) {
      return { ...byInitial[0].player, confidence: 'medium' };
    }
  }

  // Nur der Nachname
  const byLast = narrowBySuffix(entries.filter(e => e.parts.last === ocr.last));
  if (byLast.length === 1) {
    return { ...byLast[0].player, confidence: 'medium' };
  } else if (byLast.length > 1) {
    return { ...byLast[0].player, confidence: 'low' };
  }

  // Ein einzelner verlesener Buchstabe, bewusst nur 'low'
  const near = narrowBySuffix(entries.filter(e => isNearLastName(e.parts.last, ocr.last)));
  if (near.length === 1) {
    return { ...near[0].player, confidence: 'low' };
  }

  return null;
};

export default {
  extractWithGPT4Vision,
  isGPT4VisionAvailable
};
